import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { canUseDOM } from '@surfline/web-common';
import { useDispatch, useSelector } from 'react-redux';
import { computeMeterModal } from '../../../common/metering';
import { useUserDetailsSelector, useEntitlementsSelector } from '../../../selectors/user';
import { useMeterSelector } from '../../../selectors/metering';
import { performedModalRecompute, showForm } from '../../../actions';
import { getModalRecomputeFlag } from '../../../selectors/navigation';
import { HOME } from '../../../common/constants';

/**
 * @description Since we have to perform a few different fetches
 * when performing inline registration/sign-in (user info, meter info)
 * we want to defer recomputing the regwall state until all the different
 * fetches have completed successfully
 * @function useModalComputer
 * @param {import('@surfline/web-common').MeterState} meter
 * @param {import('@surfline/web-common').UserState} user
 * @param {import('@surfline/web-common').UserState['entitlements']} entitlements
 * @return {JSX.Element | null}
 */
const useModalComputer = (meter, user, entitlements) => {
  const dispatch = useDispatch();
  const shouldRecompute = useSelector(getModalRecomputeFlag);

  const [modalToRender, setModalToRender] = useState(computeMeterModal(meter, user, entitlements));

  const recomputeModal = useCallback(
    () => setModalToRender(computeMeterModal(meter, user, entitlements)),
    [meter, user, entitlements],
  );

  useEffect(() => {
    if (shouldRecompute) {
      recomputeModal();
      dispatch(performedModalRecompute());
      dispatch(showForm(HOME));
    }
  }, [shouldRecompute, dispatch, recomputeModal]);

  return modalToRender;
};

const RegWall = ({ renderOptions: { rateLimitingEnabled } }) => {
  const [ready, setReady] = useState(false);
  const entitlements = useEntitlementsSelector();
  const user = useUserDetailsSelector();
  const meter = useMeterSelector();
  const modalToRender = useModalComputer(meter, user, entitlements);

  useEffect(() => {
    if (canUseDOM && rateLimitingEnabled && meter) {
      setReady(true);
    }
    if (ready && modalToRender) {
      /* istanbul ignore next */
      // iOS scroll locking
      document.body.ontouchmove = (e) => {
        e.preventDefault();
        return false;
      };
      // web scroll locking
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    }
    return () => {
      document.body.style.position = 'relative';
    };
  }, [rateLimitingEnabled, ready, meter, modalToRender]);

  return <div>{ready && modalToRender ? modalToRender : null}</div>;
};

RegWall.propTypes = {
  renderOptions: PropTypes.shape({
    rateLimitingEnabled: PropTypes.oneOf(['true', 'false']),
  }),
};

RegWall.defaultProps = {
  renderOptions: PropTypes.shape({}),
};

export default RegWall;
